import { useCallback, useEffect, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';

import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';

import { getFragmentData, graphql } from '@e-socna/data-layer';
import type { FragmentType } from '@e-socna/data-layer';

import { useAuthenticationContext } from '../../../../contexts/AuthenticationContext/hooks/useAuthenticationContext.hook';
import { useLocalStorage } from '../../../../hooks/useLocalStorage.hook';
import { PERSISTER_BUSTER } from '../../../../components/PersistQueryClientProvider';
import { Loading } from '../../../../components/Loading';

import { DisplaySettings } from './DisplaySettings';

export const UserPage_Settings_User_Fragment = graphql(`
  fragment UserPage_Settings_User_Fragment on User {
    _id
    uiSettings {
      paletteModeId
      themeMainColor
      isContextualColorEnabled
    }
  }
`);

interface Props {
  readonly user: FragmentType<typeof UserPage_Settings_User_Fragment>;
}

export const Settings = ({ user: pUsers }: Readonly<Props>) => {
  const authenticationContext = useAuthenticationContext();
  const user = getFragmentData(UserPage_Settings_User_Fragment, pUsers);
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [_persisterBuster, setPersisterBuster] = useLocalStorage(
    PERSISTER_BUSTER,
    new Date().toISOString(),
  );
  const [isRefreshingCache, setIsRefreshingCache] = useState(false);

  useEffect(() => {
    async function refreshCache() {
      if (!isRefreshingCache) {
        return;
      }

      setPersisterBuster(new Date().toISOString());
      await queryClient.invalidateQueries();
      setIsRefreshingCache(false);
      enqueueSnackbar('Le cache a bien été rafraîchi.');
    }

    void refreshCache();
  }, [isRefreshingCache, setPersisterBuster, queryClient, enqueueSnackbar]);

  const startRefreshCache = useCallback(() => {
    setIsRefreshingCache(true);
  }, [setIsRefreshingCache]);

  const isMyProfile =
    authenticationContext.authenticationData?._id === user._id;

  return (
    <>
      {/* eslint-disable-next-line @typescript-eslint/no-unnecessary-type-conversion */}
      {!!isRefreshingCache && <Loading />}
      <Stack spacing={2} sx={{ pt: 4, width: '100%' }}>
        <DisplaySettings
          isEditable={isMyProfile}
          uiSettings={user.uiSettings}
        />
        {/* eslint-disable-next-line @typescript-eslint/no-unnecessary-type-conversion */}
        {!!isMyProfile && (
          <Paper sx={{ p: 2 }}>
            <Typography gutterBottom variant="h6">
              Cache du référentiel
            </Typography>
            <Button onClick={startRefreshCache} variant="contained">
              Rafraîchir
            </Button>
          </Paper>
        )}
      </Stack>
    </>
  );
};
